/**
 * Transactional document numbering.
 *
 * WHY THIS EXISTS AS A SEPARATE MODULE
 * -----------------------------------
 * 1.6.2 read a counter, showed the user the next value, and wrote the counter
 * back AFTER the document was saved. Two windows, or a crash between read
 * and write, duplicated a GST invoice number. The `&[businessId+docType+
 * financialYear+docNumber]` unique index turns that from a data-integrity
 * risk into a hard database constraint.
 *
 * THE THREE-PART KEY IS NOT OPTIONAL
 * ---------------------------------
 * The real 1.6.2 export proves all three parts are needed:
 *   - both companies use the SAME "26-27/" series
 *   - company_1 has BOTH "25-26" (343 invoices) and "26-27" (129)
 *   - invoices, quotations and POs each number independently
 *
 * The stored counters in that export were "0" and {} — useless. So the
 * migration MUST derive the start from MAX(docNumber); see
 * `seedSequenceFromDocuments`.
 */
import type { Table } from 'dexie';
import type { DocType } from '@domain/docTypes';
import { docSpec } from '@domain/docTypes';
import type { Document, DocumentSequence } from '../db/types';
import { financialYearFor } from '../../domain/financialYear';

export class SequenceError extends Error {
  constructor(
    readonly code: 'FY_SEALED' | 'NO_PREFIX',
    message: string,
  ) {
    super(message);
    this.name = 'SequenceError';
  }
}

export interface SequenceTables {
  documentSequences: Table<DocumentSequence, string>;
  documents: Table<Document, string>;
}

/** The three-part sequence key. Deterministic, so it is also the id. */
export function sequenceId(
  businessId: string,
  docType: DocType,
  financialYear: string,
): string {
  return `${businessId}:${docType}:${financialYear}`;
}

export interface NumberFormat {
  /** e.g. 'INV' */
  prefix: string;
  /** zero-pad width, e.g. 4 → 0042 */
  padding: number;
  /** true → `INV/2026-27/0042`; false → `INV-0042` */
  includeFinancialYear: boolean;
}

export const DEFAULT_NUMBER_FORMAT: NumberFormat = {
  prefix: 'INV',
  padding: 4,
  includeFinancialYear: true,
};

export function formatDocNumber(
  seq: number,
  financialYear: string,
  format: NumberFormat,
): string {
  const n = String(seq).padStart(format.padding, '0');
  return format.includeFinancialYear
    ? `${format.prefix}/${financialYear}/${n}`
    : `${format.prefix}-${n}`;
}

export interface AllocatedNumber {
  seq: number;
  docNumber: string;
  financialYear: string;
  sequenceId: string;
}

/**
 * Allocate the next number for a document type in a financial year.
 *
 * MUST be called inside a `db.transaction` that also writes the document,
 * so the increment rolls back with the document if anything downstream
 * fails. An allocated-but-unused number is a gap; a reused number is a legal
 * problem. Gaps are recoverable, reuse is not.
 */
export async function allocateDocNumber(
  db: SequenceTables,
  params: {
    businessId: string;
    docType: DocType;
    docDate: string;
    issuedBy: string;
    issuedAt: string;
    format?: Partial<NumberFormat>;
    fyStartMonth?: number;
  },
): Promise<AllocatedNumber> {
  const fyStartMonth = params.fyStartMonth ?? 4;
  const financialYear = financialYearFor(params.docDate, fyStartMonth);
  const id = sequenceId(params.businessId, params.docType, financialYear);
  const format: NumberFormat = {
    ...DEFAULT_NUMBER_FORMAT,
    prefix: docSpec(params.docType).numberPrefix,
    ...params.format,
  };

  const existing = await db.documentSequences.get(id);
  if (existing?.isSealed) {
    throw new SequenceError(
      'FY_SEALED',
      `The ${financialYear} series is closed. Reopen it in Settings before issuing documents.`,
    );
  }

  // Highest sequence actually ISSUED. A counter alone is not enough: the
  // real 1.6.2 export stored nextValue 0 while documents ran to 343.
  const issued = await db.documents
    .where('[businessId+docType]')
    .equals([params.businessId, params.docType])
    .filter((d) => d.financialYear === financialYear)
    .toArray();
  const maxIssued = issued.reduce((m, d) => Math.max(m, d.docNumberSeq), 0);

  // `nextValue` is the NEXT number to hand out, not the last one used. So the
  // floor is `maxIssued + 1`, and an absent sequence starts at 1. Getting this
  // wrong by one burns a number on every issue.
  const next = Math.max(existing?.nextValue ?? 1, maxIssued + 1);

  await db.documentSequences.put({
    id,
    businessId: params.businessId,
    docType: params.docType,
    financialYear,
    prefix: format.prefix,
    padding: format.padding,
    nextValue: next + 1,
    lastIssuedAt: params.issuedAt,
    lastIssuedBy: params.issuedBy,
    isSealed: existing?.isSealed ?? false,
  });

  return {
    seq: next,
    docNumber: formatDocNumber(next, financialYear, format),
    financialYear,
    sequenceId: id,
  };
}

/**
 * Seed sequences from documents that already exist.
 *
 * Used when restoring from backup: the stored counters are "0" and {}, so the
 * only trustworthy source of the next number is MAX(docNumberSeq).
 * Idempotent — re-running can never LOWER a sequence.
 */
export async function seedSequenceFromDocuments(
  db: SequenceTables,
  businessId: string,
): Promise<{ created: number; raised: number; groups: number }> {
  const all = await db.documents.where('businessId').equals(businessId).toArray();

  const groups = new Map<
    string,
    { businessId: string; docType: DocType; financialYear: string; max: number }
  >();

  for (const d of all) {
    const id = sequenceId(d.businessId, d.docType, d.financialYear);
    const g = groups.get(id);
    if (g) g.max = Math.max(g.max, d.docNumberSeq);
    else {
      groups.set(id, {
        businessId: d.businessId,
        docType: d.docType,
        financialYear: d.financialYear,
        max: d.docNumberSeq,
      });
    }
  }

  let created = 0;
  let raised = 0;

  for (const [id, g] of groups) {
    const existing = await db.documentSequences.get(id);
    const nextValue = Math.max(existing?.nextValue ?? 0, g.max + 1);
    if (!existing) created++;
    else if (nextValue > existing.nextValue) raised++;

    await db.documentSequences.put({
      id,
      businessId: g.businessId,
      docType: g.docType,
      financialYear: g.financialYear,
      prefix: existing?.prefix ?? docSpec(g.docType).numberPrefix,
      padding: existing?.padding ?? DEFAULT_NUMBER_FORMAT.padding,
      nextValue,
      isSealed: existing?.isSealed ?? false,
      lastIssuedAt: existing?.lastIssuedAt,
      lastIssuedBy: existing?.lastIssuedBy,
    });
  }

  return { created, raised, groups: groups.size };
}

/** Open or close a financial-year series. */
export async function setSequenceSealed(
  db: { documentSequences: Table<DocumentSequence, string> },
  businessId: string,
  docType: DocType,
  financialYear: string,
  isSealed: boolean,
): Promise<void> {
  const id = sequenceId(businessId, docType, financialYear);
  const existing = await db.documentSequences.get(id);
  await db.documentSequences.put({
    id,
    businessId,
    docType,
    financialYear,
    prefix: existing?.prefix ?? docSpec(docType).numberPrefix,
    padding: existing?.padding ?? DEFAULT_NUMBER_FORMAT.padding,
    nextValue: existing?.nextValue ?? 1,
    isSealed,
    lastIssuedAt: existing?.lastIssuedAt,
    lastIssuedBy: existing?.lastIssuedBy,
  });
}
